/** Small, versioned UI preferences. Business state belongs in its own stores. */
export type SemanticDatePreset =
  | 'all'
  | 'today'
  | 'yesterday'
  | 'last24Hours'
  | 'last7Days'
  | 'last30Days'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'last90Days'

export type RememberablePaymentMethod =
  | 'cash'
  | 'card'
  | 'electronic_wallet'
  | 'instapay'
  | 'bank_transfer'
  | 'payment_gateway'

export type UserPreferenceValues = {
  'branch.preferredId': string
  'branch.viewMode': 'branch' | 'global'
  'warehouse.preferredId': string
  'pos.lastCategoryId': string
  'pos.paymentMethod': RememberablePaymentMethod
  'reports.datePreset': SemanticDatePreset
  'table.pageSize': number
}

type PreferenceScope = 'tenantUser' | 'tenantUserBranch' | 'tenantUserTable'

type PreferenceDefinition<T> = {
  scope: PreferenceScope
  validate: (value: unknown) => value is T
}

const PAGE_SIZES = new Set([10, 15, 20, 25, 30, 50, 100])
const DATE_PRESETS = new Set<SemanticDatePreset>([
  'all', 'today', 'yesterday', 'last24Hours', 'last7Days', 'last30Days', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'last90Days',
])
const PAYMENT_METHODS = new Set<RememberablePaymentMethod>([
  'cash', 'card', 'electronic_wallet', 'instapay', 'bank_transfer', 'payment_gateway',
])

export function isRememberablePaymentMethod(value: unknown): value is RememberablePaymentMethod {
  return typeof value === 'string' && PAYMENT_METHODS.has(value as RememberablePaymentMethod)
}

export function resolveRememberedPaymentMethod(
  value: unknown,
  isAvailable: (method: RememberablePaymentMethod) => boolean,
): RememberablePaymentMethod | null {
  return isRememberablePaymentMethod(value) && isAvailable(value) ? value : null
}

const PREFERENCE_DEFINITIONS: { [K in keyof UserPreferenceValues]: PreferenceDefinition<UserPreferenceValues[K]> } = {
  'branch.preferredId': { scope: 'tenantUser', validate: (value): value is string => typeof value === 'string' && value.trim().length > 0 },
  'branch.viewMode': { scope: 'tenantUser', validate: (value): value is 'branch' | 'global' => value === 'branch' || value === 'global' },
  'warehouse.preferredId': { scope: 'tenantUserBranch', validate: (value): value is string => typeof value === 'string' && value.trim().length > 0 },
  'pos.lastCategoryId': { scope: 'tenantUserBranch', validate: (value): value is string => typeof value === 'string' && (value === 'all' || value.trim().length > 0) },
  'pos.paymentMethod': { scope: 'tenantUserBranch', validate: isRememberablePaymentMethod },
  'reports.datePreset': { scope: 'tenantUser', validate: (value): value is SemanticDatePreset => typeof value === 'string' && DATE_PRESETS.has(value as SemanticDatePreset) },
  'table.pageSize': { scope: 'tenantUserTable', validate: (value): value is number => typeof value === 'number' && PAGE_SIZES.has(value) },
}

export type UserPreferenceKey = keyof UserPreferenceValues

export type PreferenceContext = {
  branchId?: string | number | null
  tableId?: string | null
}

export const USER_PREFERENCE_SCHEMA_VERSION = 1
const STORAGE_NAMESPACE = 'madar:user-preferences'
const PREFERENCE_CHANGED_EVENT = 'madar:user-preferences:changed'

type PreferenceEnvelope = {
  version: number
  value: unknown
  updatedAt: number
}

type StoredAuthData = {
  token?: string
  tenant_slug?: string
  user?: {
    id?: string | number | null
    tenant_id?: string | number | null
    tenant_uuid?: string | null
  }
  id?: string | number | null
  tenant_id?: string | number | null
  tenant_uuid?: string | null
}

type PreferenceIdentity = { tenant: string; user: string }

function safePart(value: string | number): string {
  return encodeURIComponent(String(value).trim())
}

/** Tenant slug is preferred because IDs may collide between tenants. */
export function getCurrentPreferenceIdentity(): PreferenceIdentity | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem('authUser')
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAuthData
    const userId = parsed.user?.id ?? parsed.id
    const tenant = localStorage.getItem('tenant_slug')?.trim()
      || parsed.tenant_slug?.trim()
      || (parsed.user?.tenant_uuid ? `uuid:${parsed.user.tenant_uuid}` : null)
      || (parsed.user?.tenant_id != null ? `id:${parsed.user.tenant_id}` : null)
      || (parsed.tenant_uuid ? `uuid:${parsed.tenant_uuid}` : null)
      || (parsed.tenant_id != null ? `id:${parsed.tenant_id}` : null)
    if (userId == null || tenant == null || !String(userId).trim() || !tenant.trim()) return null
    return { tenant, user: String(userId) }
  } catch {
    return null
  }
}

export function getCurrentPreferenceIdentityKey(): string | null {
  const identity = getCurrentPreferenceIdentity()
  if (!identity) return null
  return `${safePart(identity.tenant)}:${safePart(identity.user)}`
}

export function getUserPreferenceStorageKey<K extends UserPreferenceKey>(
  key: K,
  context: PreferenceContext = {},
): string | null {
  const identity = getCurrentPreferenceIdentity()
  if (!identity) return null
  const definition = PREFERENCE_DEFINITIONS[key]
  const parts = [
    STORAGE_NAMESPACE,
    `tenant:${safePart(identity.tenant)}`,
    `user:${safePart(identity.user)}`,
    `v${USER_PREFERENCE_SCHEMA_VERSION}`,
  ]
  if (definition.scope === 'tenantUserBranch') {
    if (context.branchId == null || !String(context.branchId).trim()) return null
    parts.push(`branch:${safePart(context.branchId)}`)
  }
  if (definition.scope === 'tenantUserTable') {
    if (!context.tableId?.trim()) return null
    parts.push(`table:${safePart(context.tableId)}`)
  }
  parts.push(key)
  return parts.join(':')
}

function emitPreferenceChange(key: string | null): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(PREFERENCE_CHANGED_EVENT, { detail: { key } }))
}

export function getUserPreference<K extends UserPreferenceKey>(
  key: K,
  context: PreferenceContext = {},
): UserPreferenceValues[K] | null {
  const storageKey = getUserPreferenceStorageKey(key, context)
  if (!storageKey || typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const envelope = JSON.parse(raw) as Partial<PreferenceEnvelope>
    const definition = PREFERENCE_DEFINITIONS[key]
    if (envelope.version !== USER_PREFERENCE_SCHEMA_VERSION || !definition.validate(envelope.value)) {
      localStorage.removeItem(storageKey)
      emitPreferenceChange(storageKey)
      return null
    }
    return envelope.value
  } catch {
    try { localStorage.removeItem(storageKey) } catch { /* storage is optional */ }
    return null
  }
}

export function setUserPreference<K extends UserPreferenceKey>(
  key: K,
  value: UserPreferenceValues[K],
  context: PreferenceContext = {},
): boolean {
  const storageKey = getUserPreferenceStorageKey(key, context)
  const definition = PREFERENCE_DEFINITIONS[key]
  if (!storageKey || !definition.validate(value) || typeof localStorage === 'undefined') return false
  try {
    const envelope: PreferenceEnvelope = {
      version: USER_PREFERENCE_SCHEMA_VERSION,
      value,
      updatedAt: Date.now(),
    }
    localStorage.setItem(storageKey, JSON.stringify(envelope))
    emitPreferenceChange(storageKey)
    return true
  } catch {
    return false
  }
}

export function removeUserPreference<K extends UserPreferenceKey>(
  key: K,
  context: PreferenceContext = {},
): void {
  const storageKey = getUserPreferenceStorageKey(key, context)
  if (!storageKey || typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(storageKey)
    emitPreferenceChange(storageKey)
  } catch { /* storage is optional */ }
}

/** Reset only this signed-in user's UI preference namespace, never offline data. */
export function resetCurrentUserPreferences(): number {
  const identity = getCurrentPreferenceIdentity()
  if (!identity || typeof localStorage === 'undefined') return 0
  const prefix = `${STORAGE_NAMESPACE}:tenant:${safePart(identity.tenant)}:user:${safePart(identity.user)}:`
  const keys: string[] = []
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (key?.startsWith(prefix)) keys.push(key)
    }
    keys.forEach((key) => localStorage.removeItem(key))
    emitPreferenceChange(null)
    return keys.length
  } catch {
    return 0
  }
}

export function subscribeToUserPreferenceChanges(listener: (key: string | null) => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  const onCustomChange = (event: Event) => {
    const detail = (event as CustomEvent<{ key?: string | null }>).detail
    listener(detail?.key ?? null)
  }
  const onStorage = (event: StorageEvent) => {
    if (
      event.key?.startsWith(STORAGE_NAMESPACE)
      || event.key === 'authUser'
      || event.key === 'tenant_slug'
      || event.key === null
    ) listener(event.key)
  }
  const onContextChange = () => listener(null)
  window.addEventListener(PREFERENCE_CHANGED_EVENT, onCustomChange)
  window.addEventListener('storage', onStorage)
  window.addEventListener('auth:updated', onContextChange)
  window.addEventListener('branch:changed', onContextChange)
  return () => {
    window.removeEventListener(PREFERENCE_CHANGED_EVENT, onCustomChange)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('auth:updated', onContextChange)
    window.removeEventListener('branch:changed', onContextChange)
  }
}
